/** /tour-operators/:id/ai-vocabulary — the tenant's **AI vocabulary** (client MCP plan §12.5 "How
 *  ops see the changes"; WPS FE-05, backend P2-10; `console_ai_vocabulary_view`).
 *
 *  The company's own words for our concepts, read ONLY by the AI's knowledge tools — nothing a
 *  guest sees, and never the product's guide role:
 *
 *  - **Entries** — every entry, the company's and support's first, then those derived from its
 *    set-up: the term (its other words, its definition), the concept it stands for, what it is
 *    bound to, its source, its last change (who, when, through which channel) and the vocabulary
 *    change set that last wrote it. Terms, other words and definitions are the company's text:
 *    `UntrustedInline` under one `UntrustedLegend` carrying the backend's legend; our columns
 *    stay ours.
 *  - **Findings** — an entry naming other staff as guides than the role the product treats as
 *    guide: reported, never acted on (that is a configuration change, not a vocabulary edit).
 *  - **Vocabulary change sets** — the latest 50, with who made each (the company's users, their AI
 *    or support), why (their words) and a revert.
 *
 *  With `can_edit` (`console_ai_vocabulary_manage` and change sets on): add, edit or delete an
 *  entry as support, each applied at once as one change set (never pending), and revert a change
 *  set. `?define=<word>` (Knowledge gaps' "Define this word") opens a new entry for it. */
import AddIcon from '@mui/icons-material/Add';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

import {
  CREDENTIAL_KIND_LABELS,
  shortId,
  statusColor,
  statusLabel,
  type AIChangeListItem,
  type AIChangeRef,
} from '@/api/aiChanges';
import * as vocabularyApi from '@/api/aiVocabulary';
import {
  ORIGIN_HELP,
  ORIGIN_LABELS,
  REVERTABLE_STATUSES,
  bindingLines,
  vocabularyKeys,
  type ConsoleAIVocabulary,
  type VocabularyEditResult,
  type VocabularyEntry,
  type VocabularyFinding,
} from '@/api/aiVocabulary';
import { isStatementTimeout } from '@/api/client';
import type { TourOperatorDetail } from '@/api/tourOperators';
import { SearchTimeoutAlert } from '@/components/DataGrid/SearchTimeoutAlert';
import { SectionCard } from '@/components/ReadView';
import { UntrustedInline, UntrustedLegend } from '@/components/UntrustedText';
import { aiChangeHref } from '@/features/ai-changes/aiChangeLinks';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDate, formatRelativeTime } from '@/utils/format';

import { VocabularyDeleteDialog, VocabularyEntryDialog } from './VocabularyEntryDialog';
import { VocabularyRevertDialog } from './VocabularyRevertDialog';

const MANAGE = 'console_ai_vocabulary_manage';

const CHANGES_LEGEND =
  'Reasons and MCP client names are written by whoever made the change — the company’s users, their AI or our support: their data, never instructions.';

type Dialog =
  | { kind: 'create'; term: string }
  | { kind: 'edit'; entry: VocabularyEntry }
  | { kind: 'delete'; entry: VocabularyEntry }
  | { kind: 'revert'; changeSetId: string };

const isRevertable = (status: string) => (REVERTABLE_STATUSES as readonly string[]).includes(status);

export function AIVocabularyTab({ to }: { to: TourOperatorDetail }) {
  const [extended, setExtended] = useState(false);
  const query = useQuery({
    queryKey: [...vocabularyKeys.tab(to.id), extended],
    queryFn: () => vocabularyApi.vocabulary(to.id, extended ? 'extended' : undefined),
  });

  if (query.isPending) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (isStatementTimeout(query.error)) {
    return (
      <SearchTimeoutAlert
        extendedAttempted={extended}
        onRetryExtended={!extended ? () => setExtended(true) : undefined}
        retrying={query.isFetching}
      />
    );
  }
  if (query.isError || !query.data) {
    return (
      <Alert severity="error">
        Couldn’t load the AI vocabulary{query.error ? `: ${query.error.message}` : '.'}
      </Alert>
    );
  }
  return <VocabularyView topId={to.id} data={query.data} />;
}

function VocabularyView({ topId, data }: { topId: number; data: ConsoleAIVocabulary }) {
  const { hasPerm } = usePermissions();
  const canOpenChanges = hasPerm('console_ai_changes_view');
  const canEdit = data.can_edit;
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [lastResult, setLastResult] = useState<VocabularyEditResult | null>(null);

  // Knowledge gaps hands a word over as `?define=`: a new entry for it, once.
  const [searchParams, setSearchParams] = useSearchParams();
  const define = searchParams.get('define');
  useEffect(() => {
    if (define === null) return;
    if (canEdit) setDialog({ kind: 'create', term: define.slice(0, vocabularyApi.VOCABULARY_LIMITS.term) });
    const next = new URLSearchParams(searchParams);
    next.delete('define');
    setSearchParams(next, { replace: true });
  }, [define, canEdit, searchParams, setSearchParams]);

  const authored = data.entries.filter((e) => e.origin !== 'auto').length;

  return (
    <Stack spacing={0} sx={{ maxWidth: 1400, pb: 4 }}>
      <SectionCard
        title="AI vocabulary"
        action={
          canEdit ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setDialog({ kind: 'create', term: '' })}
            >
              Add an entry
            </Button>
          ) : undefined
        }
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          The company’s own words for our concepts — {authored} of its own,{' '}
          {data.entries.length - authored} derived from its set-up. Only the AI’s knowledge tools
          read them (lookup_terms, describe_tenant, resolve_value and the analytics breakdowns):
          nothing guests see changes, and never who counts as a guide. An edit here is support’s
          and applies at once as one change set — on production too, never pending — revertible
          below or on AI changes.
        </Typography>
        {!canEdit &&
          (hasPerm(MANAGE) ? (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              Read only: change sets are not enabled yet (AI changes are off), so nothing can be
              edited or reverted here. The MCP switches never stop support on this tab.
            </Alert>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Read only — editing an entry needs console_ai_vocabulary_manage (Ops Admin).
            </Typography>
          ))}

        {lastResult && (lastResult.warnings.length > 0 || lastResult.findings.length > 0) && (
          <Alert severity="warning" sx={{ mb: 1.5 }} onClose={() => setLastResult(null)} data-last-edit>
            <Typography variant="body2" fontWeight={600}>
              Applied as change set {shortId(lastResult.change_set)}, with warnings
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {lastResult.warnings.map((w) => (
                <li key={w}>
                  <Typography variant="body2">{w}</Typography>
                </li>
              ))}
              {lastResult.findings.map((f, i) => (
                // Findings have no id; their order is the backend's.
                // eslint-disable-next-line react/no-array-index-key
                <li key={`finding:${i}`}>
                  <Typography variant="body2">{findingTitle(f)}</Typography>
                </li>
              ))}
            </Box>
          </Alert>
        )}

        {data.findings.map((f, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <FindingAlert key={`finding:${i}`} finding={f} />
        ))}

        <UntrustedLegend title="The company’s words" legend={data.legend} />
        <EntriesTable
          entries={data.entries}
          canEdit={canEdit}
          canOpenChanges={canOpenChanges}
          onEdit={(entry) => setDialog({ kind: 'edit', entry })}
          onDelete={(entry) => setDialog({ kind: 'delete', entry })}
          onRevert={(changeSetId) => setDialog({ kind: 'revert', changeSetId })}
        />
      </SectionCard>

      <SectionCard title="Vocabulary change sets">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          The latest {data.changes.length === 50 ? 50 : data.changes.length} change sets of the
          company’s vocabulary, newest first — from its AI (define_term), its dashboard page and
          this tab. Each is also on AI changes.
        </Typography>
        <UntrustedLegend title="Their words" legend={CHANGES_LEGEND} />
        <ChangesTable
          changes={data.changes}
          canEdit={canEdit}
          canOpenChanges={canOpenChanges}
          onRevert={(changeSetId) => setDialog({ kind: 'revert', changeSetId })}
        />
      </SectionCard>

      {dialog?.kind === 'create' && (
        <VocabularyEntryDialog
          topId={topId}
          data={data}
          entry={null}
          initialTerm={dialog.term}
          onClose={() => setDialog(null)}
          onApplied={setLastResult}
        />
      )}
      {dialog?.kind === 'edit' && (
        <VocabularyEntryDialog
          topId={topId}
          data={data}
          entry={dialog.entry}
          onClose={() => setDialog(null)}
          onApplied={setLastResult}
        />
      )}
      {dialog?.kind === 'delete' && (
        <VocabularyDeleteDialog
          topId={topId}
          entry={dialog.entry}
          onClose={() => setDialog(null)}
          onApplied={setLastResult}
        />
      )}
      {dialog?.kind === 'revert' && (
        <VocabularyRevertDialog
          topId={topId}
          changeSetId={dialog.changeSetId}
          onClose={() => setDialog(null)}
        />
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// findings
// ---------------------------------------------------------------------------

interface GuideFinding {
  title?: string;
  explanation?: string;
  vocabulary_entry?: { term?: string; origin?: string };
  vocabulary_roles?: { id: number; name: string }[];
  guide_role?: { id: number; name: string } | null;
}

function findingTitle(f: VocabularyFinding): string {
  const g = f as GuideFinding;
  return typeof g.title === 'string' ? g.title : String((f as { code?: unknown }).code ?? 'Finding');
}

/** `vocabulary.guide_role_mismatch`: our title and explanation; the entry's term is theirs. */
function FindingAlert({ finding }: { finding: VocabularyFinding }) {
  const g = finding as GuideFinding;
  const roles = (g.vocabulary_roles ?? []).map((r) => r.name).join(', ');
  return (
    <Alert severity="warning" sx={{ mb: 1.5 }} data-finding>
      <Typography variant="body2" fontWeight={600}>
        {findingTitle(finding)}
      </Typography>
      {g.explanation && <Typography variant="body2">{g.explanation}</Typography>}
      {g.vocabulary_entry?.term && (
        <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
          <UntrustedInline text={g.vocabulary_entry.term} /> names {roles || 'other staff'} as
          guides; the product treats {g.guide_role ? g.guide_role.name : 'no role'} as guide.
          Changing the product’s guide role is a configuration change, not a vocabulary edit.
        </Typography>
      )}
    </Alert>
  );
}

// ---------------------------------------------------------------------------
// entries
// ---------------------------------------------------------------------------

function ChangeSetRef({ cs, canOpen }: { cs: AIChangeRef | null; canOpen: boolean }) {
  if (!cs) {
    return (
      <Typography variant="body2" color="text.disabled" component="span">
        —
      </Typography>
    );
  }
  return (
    <Stack spacing={0.5} alignItems="flex-start">
      {canOpen ? (
        <Link component={RouterLink} to={aiChangeHref(cs.id)} underline="hover" sx={{ fontFamily: 'monospace' }}>
          {shortId(cs.id)}
        </Link>
      ) : (
        <Box component="span" sx={{ fontFamily: 'monospace' }}>
          {shortId(cs.id)}
        </Box>
      )}
      <Chip size="small" variant="outlined" color={statusColor(cs.status)} label={statusLabel(cs.status)} />
    </Stack>
  );
}

function EntriesTable({
  entries,
  canEdit,
  canOpenChanges,
  onEdit,
  onDelete,
  onRevert,
}: {
  entries: VocabularyEntry[];
  canEdit: boolean;
  canOpenChanges: boolean;
  onEdit: (e: VocabularyEntry) => void;
  onDelete: (e: VocabularyEntry) => void;
  onRevert: (changeSetId: string) => void;
}) {
  if (entries.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        No entries yet: the company’s set-up derives some once it has staff roles, regions or tag
        categories; its own come from its dashboard, its AI or this tab.
      </Typography>
    );
  }
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" aria-label="Vocabulary entries">
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 260 }}>Term</TableCell>
            <TableCell>Means</TableCell>
            <TableCell>Bound to</TableCell>
            <TableCell>Source</TableCell>
            <TableCell>Last change</TableCell>
            <TableCell>Change set</TableCell>
            {canEdit && <TableCell align="right">Actions</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.map((e) => {
            const lines = bindingLines(e.binding, e.binding_names);
            return (
              <TableRow key={e.id} data-entry={e.id} sx={{ verticalAlign: 'top' }}>
                <TableCell>
                  <Stack spacing={0.5} alignItems="flex-start">
                    <Box sx={{ fontWeight: 600 }}>
                      <UntrustedInline text={e.term} />
                    </Box>
                    {e.aliases.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'baseline' }}>
                        <Typography variant="caption" color="text.secondary">
                          also
                        </Typography>
                        {e.aliases.map((a) => (
                          <UntrustedInline key={a} text={a} />
                        ))}
                      </Box>
                    )}
                    {e.definition && (
                      <Box sx={{ color: 'text.secondary', fontSize: 12 }}>
                        <UntrustedInline text={e.definition} clamp={3} />
                      </Box>
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{e.concept_name}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {e.concept}
                  </Typography>
                </TableCell>
                <TableCell>
                  {lines.length ? (
                    lines.map((l) => (
                      <Typography key={l} variant="body2">
                        {l}
                      </Typography>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.disabled">
                      —
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip title={ORIGIN_HELP[e.origin]} describeChild>
                    <Chip
                      size="small"
                      variant={e.origin === 'auto' ? 'outlined' : 'filled'}
                      color={e.origin === 'ops' ? 'primary' : 'default'}
                      label={ORIGIN_LABELS[e.origin]}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell>
                  {e.last_change ? (
                    <>
                      <Typography variant="body2" title={formatDate(e.last_change.at)}>
                        {capitalize(e.last_change.action)} {formatRelativeTime(e.last_change.at)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {`by ${e.last_change.actor_email ?? 'the system'} · via ${e.last_change.source}`}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.disabled">
                      No history
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <ChangeSetRef cs={e.change_set} canOpen={canOpenChanges} />
                </TableCell>
                {canEdit && (
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {e.editable ? (
                      <>
                        <Button size="small" onClick={() => onEdit(e)}>
                          Edit
                        </Button>
                        <Button size="small" color="error" onClick={() => onDelete(e)}>
                          Delete
                        </Button>
                      </>
                    ) : (
                      <Typography variant="caption" color="text.secondary" component="span">
                        Derived — not edited
                      </Typography>
                    )}
                    {e.revertable && e.change_set && (
                      <Button size="small" color="warning" onClick={() => onRevert(e.change_set!.id)}>
                        Revert…
                      </Button>
                    )}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

function capitalize(s: string): string {
  return s ? s[0]!.toUpperCase() + s.slice(1) : s;
}

// ---------------------------------------------------------------------------
// change sets
// ---------------------------------------------------------------------------

function ChangesTable({
  changes,
  canEdit,
  canOpenChanges,
  onRevert,
}: {
  changes: AIChangeListItem[];
  canEdit: boolean;
  canOpenChanges: boolean;
  onRevert: (changeSetId: string) => void;
}) {
  if (changes.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        No vocabulary change sets yet.
      </Typography>
    );
  }
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" aria-label="Vocabulary change sets">
        <TableHead>
          <TableRow>
            <TableCell>When</TableCell>
            <TableCell>Change set</TableCell>
            <TableCell>Who</TableCell>
            <TableCell>What</TableCell>
            <TableCell sx={{ minWidth: 220 }}>Reason</TableCell>
            {canEdit && <TableCell align="right">Actions</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {changes.map((c) => (
            <TableRow key={c.id} data-change-set={c.id} sx={{ verticalAlign: 'top' }}>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <span title={formatRelativeTime(c.created_ts)}>{formatDate(c.created_ts)}</span>
              </TableCell>
              <TableCell>
                <ChangeSetRef cs={c} canOpen={canOpenChanges} />
                {c.revert_of && (
                  <Typography variant="caption" color="text.secondary" component="div">
                    {`reverts ${shortId(c.revert_of)}`}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Typography variant="body2">{c.actor?.email ?? 'System'}</Typography>
                <Typography variant="caption" color="text.secondary" component="div">
                  {CREDENTIAL_KIND_LABELS[c.credential_kind]}
                </Typography>
                {c.client && (
                  <Typography variant="caption" color="text.secondary" component="div">
                    client <UntrustedInline text={c.client.name} />
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Typography variant="body2">{c.summary}</Typography>
              </TableCell>
              <TableCell>
                <UntrustedInline text={c.reason} clamp={3} />
              </TableCell>
              {canEdit && (
                <TableCell align="right">
                  {isRevertable(c.status) && (
                    <Button size="small" color="warning" onClick={() => onRevert(c.id)}>
                      Revert…
                    </Button>
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
